import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, API_BASE } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import RouteMap from "../components/RouteMap";
import StatusBadge, { UrgencyBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Star, MapPin, Calendar, Route, Euro, Clock, Truck,
  CheckCircle2, Calculator, ChevronDown,
  Send, AlertTriangle, X, Lock, ArrowRight,
  Camera,
} from "lucide-react";
import { toast } from "sonner";

// ── Mandat de paiement : acompte séquestré + débit différé du solde (DSP2) ──
const DEPOSIT_PCT = 0.25;         // aligné sur DR_DEPOSIT_PCT (backend/finance.py)
const BALANCE_CAPTURE_DAYS = 3;   // solde débité automatiquement à J-3

function MandateModal({ offer, request, loading, onConfirm, onClose }) {
  const [accepted, setAccepted] = useState(false);
  const price = offer.price_eur;
  const fullCapture = (new Date(request.pickup_date) - Date.now()) <= BALANCE_CAPTURE_DAYS * 864e5;
  const deposit = fullCapture ? price : Math.round(price * DEPOSIT_PCT * 100) / 100;
  const balance = Math.round((price - deposit) * 100) / 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="bg-slate-900 px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-0.5">Paiement séquestré</div>
            <div className="font-display font-extrabold text-white text-2xl">{price.toFixed(2)} € TTC</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm">
            <div className="flex justify-between px-4 py-2.5">
              <span className="text-slate-600">{fullCapture ? "Paiement (départ sous 3 jours)" : "Acompte débité aujourd'hui"}</span>
              <span className="font-bold text-slate-900">{deposit.toFixed(2)} €</span>
            </div>
            {!fullCapture && (
              <div className="flex justify-between px-4 py-2.5">
                <span className="text-slate-600">Solde débité automatiquement à J-3</span>
                <span className="font-bold text-slate-900">{balance.toFixed(2)} €</span>
              </div>
            )}
          </div>

          <ul className="text-xs text-slate-600 space-y-1.5 list-disc pl-4">
            <li>Les fonds sont placés en <strong>séquestre</strong> et reversés au dépanneur après livraison prouvée par photos.</li>
            <li>L'acompte est <strong>non remboursable</strong> selon la politique d'annulation (gratuite plus de 72 h avant chargement, hors frais de dossier).</li>
            {!fullCapture && (
              <li>J'autorise expressément l'<strong>enregistrement de ma carte</strong> et le <strong>débit différé du solde</strong> 3 jours avant le chargement, sans nouvelle intervention de ma part.</li>
            )}
          </ul>

          <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={e => setAccepted(e.target.checked)}
              className="mt-0.5 accent-orange-600"
              data-testid="mandate-checkbox"
            />
            <span>
              J'ai lu et j'accepte les <a href="/cgv" target="_blank" rel="noreferrer" className="text-orange-600 underline">clauses financières (CGV)</a>,
              dont la politique d'acompte et {fullCapture ? "de séquestre" : "le mandat de débit différé du solde"}.
            </span>
          </label>

          <button
            onClick={onConfirm}
            disabled={!accepted || loading}
            className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-display font-bold text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="mandate-confirm"
          >
            {loading ? (
              <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Redirection sécurisée…</>
            ) : (
              <><Lock className="w-4 h-4" />Payer {deposit.toFixed(2)} € {fullCapture ? "" : "d'acompte"} — paiement sécurisé</>
            )}
          </button>
          <p className="text-[10px] text-center text-slate-400">
            Encaissement et séquestre opérés par Stripe. Aucune donnée de carte ne transite par DepannRoute.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Modal d'annulation client : aperçu chiffré de la politique 72 h ──
const CANCEL_TIER_LABELS = {
  NO_FUNDS: "Aucun paiement effectué — annulation gratuite",
  FREE: "Plus de 72 h avant chargement — remboursé hors frais de dossier",
  DEPOSIT_KEPT: "Entre 24 h et 72 h — acompte conservé, solde remboursé",
  LATE: "Moins de 24 h — acompte conservé et retenue sur le solde",
};

function CancelModal({ plan, loading, onConfirm, onClose }) {
  const eur = (c) => `${((c || 0) / 100).toFixed(2)} €`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="bg-slate-900 px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-0.5">Annulation</div>
            <div className="font-display font-extrabold text-white text-xl">Politique 72 h</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-xs text-slate-600">{CANCEL_TIER_LABELS[plan.tier] || plan.tier}</p>
          {plan.captured_cents > 0 ? (
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm">
              <div className="flex justify-between px-4 py-2.5">
                <span className="text-slate-600">Payé (en séquestre)</span>
                <span className="font-bold text-slate-900">{eur(plan.captured_cents)}</span>
              </div>
              <div className="flex justify-between px-4 py-2.5">
                <span className="text-slate-600">Remboursé</span>
                <span className="font-bold text-emerald-700">{eur(plan.refund_cents)}</span>
              </div>
              {plan.kept_cents > 0 && (
                <div className="px-4 py-2.5">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Conservé</span>
                    <span className="font-bold text-red-600">−{eur(plan.kept_cents)}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    dont frais de dossier {eur(plan.platform_fee_cents)}
                    {plan.transporter_indemnity_cents > 0 && <> et indemnité dépanneur {eur(plan.transporter_indemnity_cents)}</>}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
              Aucun montant n'a été débité : l'annulation est sans frais.
            </div>
          )}
          <button
            onClick={onConfirm}
            disabled={loading}
            className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-display font-bold text-sm flex items-center justify-center gap-2"
            data-testid="cancel-confirm"
          >
            {loading ? "Annulation…" : "Confirmer l'annulation"}
          </button>
          <button onClick={onClose} className="w-full text-xs text-slate-500 hover:text-slate-700">
            Garder ma demande
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Panneau séquestre & paiement (états de la machine v2.0) ──────
export const MISSION_STATE_LABELS = {
  CREATED: ["En négociation", "bg-slate-100 text-slate-700"],
  VALIDATED: ["Acompte séquestré", "bg-amber-100 text-amber-800"],
  AWAITING_BALANCE: ["Solde débité à J-3", "bg-amber-100 text-amber-800"],
  BALANCE_CAPTURED: ["Séquestre complet", "bg-emerald-100 text-emerald-800"],
  AWAITING_PICKUP: ["En attente de chargement", "bg-emerald-100 text-emerald-800"],
  LOADED: ["Véhicule chargé", "bg-emerald-100 text-emerald-800"],
  DELIVERY_PENDING_CONFIRMATION: ["Livraison à confirmer", "bg-blue-100 text-blue-800"],
  DELIVERED: ["Livrée", "bg-emerald-100 text-emerald-800"],
  SETTLED: ["Clôturée — fonds reversés", "bg-emerald-100 text-emerald-800"],
  CANCELLED: ["Annulée", "bg-red-100 text-red-700"],
  CANCELLED_BY_TRANSPORTER: ["Annulée par le dépanneur", "bg-red-100 text-red-700"],
  CANCELLED_PAYMENT_FAILURE: ["Annulée — défaut de paiement", "bg-red-100 text-red-700"],
  DISPUTED: ["Litige en cours", "bg-red-100 text-red-700"],
  DISPUTE_ESCALATED: ["Litige escaladé", "bg-red-100 text-red-700"],
  DISPUTE_RESOLVED: ["Litige résolu", "bg-blue-100 text-blue-800"],
};

function PaymentPanel({ missionInfo, viewer }) {
  const m = missionInfo?.mission;
  const p = missionInfo?.payment;
  if (!m || !p || !p.amount_total_cents) return null;
  const eur = (c) => `${(c / 100).toFixed(2)} €`;
  const [label, chip] = MISSION_STATE_LABELS[m.state] || [m.state, "bg-slate-100 text-slate-700"];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="payment-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Séquestre & paiement</div>
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${chip}`}>{label}</span>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-500">Prix total TTC</span>
          <span className="font-bold text-slate-900">{eur(p.amount_total_cents)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">{p.full_capture ? "Paiement (totalité)" : "Acompte (25 %)"}</span>
          <span className={p.deposit_captured_at ? "font-semibold text-emerald-700" : "text-slate-700"}>
            {eur(p.amount_deposit_cents)} {p.deposit_captured_at ? "✓ séquestré" : "· à payer"}
          </span>
        </div>
        {!p.full_capture && (
          <div className="flex justify-between">
            <span className="text-slate-500">Solde (auto à J-3)</span>
            <span className={p.balance_captured_at ? "font-semibold text-emerald-700" : "text-slate-700"}>
              {eur(p.amount_balance_cents)} {p.balance_captured_at ? "✓ séquestré" : p.balance_retry_count > 0 ? `· échec ×${p.balance_retry_count}` : "· programmé"}
            </span>
          </div>
        )}
        {viewer === "pro" && (
          <>
            <div className="border-t border-slate-100 pt-2 flex justify-between">
              <span className="text-slate-500">Commission plateforme</span>
              <span className="text-slate-700">− {eur(p.amount_commission_cents)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Net dépanneur (payout T+2/T+3)</span>
              <span className="font-bold text-slate-900">{eur(p.amount_transporter_cents)}</span>
            </div>
          </>
        )}
      </div>
      {p.provider === "simulated" && (
        <div className="mt-3 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
          Mode démo : aucun débit réel (Stripe non configuré sur cet environnement).
        </div>
      )}
    </div>
  );
}

// ── Modèle de coût pro (même logique que NewRequest) ─────────────
const COST_BASE_D = {
  vitesseAutoroute: 110, vitesseNationale: 70,
  consoAutorouteL100: 25, consoNationaleL100: 28,
  prixCarburantLitre: 1.85, tempsOperationsH: 1.25,
  forfaitMission: 60, coutHoraire: 35, coutKmUsure: 0.18, margePct: 15,
};
const URG_COEFF = { low: 0.90, medium: 1.0, high: 1.25, emergency: 1.50 };

function calcEst(km, vehicleType, urgency) {
  const kmI = Math.round(parseFloat(km) || 0);
  if (!kmI) return null;
  const b = COST_BASE_D;
  const uM = URG_COEFF[urgency] ?? 1;
  // Hypothèse 60% autoroute par défaut si pas d'info OSRM
  const kmAutoroute = Math.round(kmI * 0.60);
  const kmNationale = kmI - kmAutoroute;
  const tempsRouteH = (kmAutoroute / b.vitesseAutoroute) + (kmNationale / b.vitesseNationale);
  const tempsTotalH = tempsRouteH + b.tempsOperationsH;
  const litres = (kmAutoroute * b.consoAutorouteL100 / 100) + (kmNationale * b.consoNationaleL100 / 100);
  const sousTotal = (b.forfaitMission + tempsTotalH * b.coutHoraire + litres * b.prixCarburantLitre + kmI * b.coutKmUsure) * uM;
  const med = Math.round((sousTotal + sousTotal * b.margePct / 100));
  return { med, min: Math.round(med * 0.88), max: Math.round(med * 1.12), km: kmI, urgMult: uM };
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit",
    });
  } catch { return iso; }
}

// ── Estimation box (client view) ─────────────────────────────────
function ClientEstimateBox({ req }) {
  const [open, setOpen] = useState(false);
  const est = useMemo(
    () => calcEst(req?.distance_km, req?.vehicle_type, req?.urgency),
    [req?.distance_km, req?.vehicle_type, req?.urgency]
  );
  if (!est) return null;

  const budgetN = parseFloat(req?.budget_eur) || 0;
  const diff    = budgetN > 0 ? Math.round((budgetN - est.med) / est.med * 100) : null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3"
        onClick={() => setOpen(v => !v)}
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
            <Calculator className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="text-left">
            <div className="text-xs font-bold text-slate-900">Estimation marché 2026</div>
            <div className="text-[11px] text-slate-500">Plateau individuel · {est.km} km · {est.min}–{est.max} €</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-display font-extrabold text-xl text-orange-600">{est.med} €</span>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-amber-200 pt-3 space-y-2 text-sm">
          <div className="flex justify-between text-slate-500">
            <span>Coût estimé (carburant, temps, usure, marge)</span>
            <span className="font-medium text-slate-700">{est.med} €</span>
          </div>
          {est.urgMult !== 1.0 && (
            <div className="flex justify-between text-slate-500">
              <span>Coefficient urgence</span>
              <span className="font-medium text-amber-600">×{est.urgMult}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-amber-200 font-bold">
            <span className="text-slate-900">Fourchette estimée</span>
            <span className="text-orange-600 font-display text-base">{est.min}–{est.max} €</span>
          </div>
        </div>
      )}

      {diff !== null && (
        <div className={`mx-4 mb-3 flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg ${
          diff >= 0 ? "bg-emerald-100 text-emerald-700"
          : diff >= -20 ? "bg-amber-100 text-amber-700"
          : "bg-red-100 text-red-700"
        }`}>
          {diff >= 0
            ? `✓ Budget ${diff}% au-dessus de la médiane — offres rapides`
            : diff >= -20
            ? `⚠ Budget ${Math.abs(diff)}% sous la médiane — recevable`
            : `✗ Budget ${Math.abs(diff)}% sous la médiane — peu de réponses`
          }
        </div>
      )}
    </div>
  );
}

// ── Stars helper ─────────────────────────────────────────────────
function Stars({ n }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`w-2.5 h-2.5 ${i <= Math.round(n) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
      ))}
    </span>
  );
}

// ── Offer card (client view) ──────────────────────────────────────
function OfferCard({ offer, canAccept, onAccept, onRefuse, onCounter, onAbandon, counterBy }) {
  const [showCounter, setShowCounter] = useState(false);
  const [counterPrice, setCounterPrice] = useState("");
  const [counterMsg,   setCounterMsg]   = useState("");
  const [counterLoading, setCounterLoading] = useState(false);

  const ini = (offer.pro_name || "?")
    .split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  const submitCounter = async () => {
    if (!counterPrice || parseFloat(counterPrice) <= 0) return;
    setCounterLoading(true);
    try {
      await onCounter(offer.offer_id, parseFloat(counterPrice), counterMsg);
      setShowCounter(false);
      setCounterPrice("");
      setCounterMsg("");
    } finally {
      setCounterLoading(false);
    }
  };

  const isNegotiable = offer.status === "pending" || offer.status === "countered";
  const lastCounterBy = offer.counter_history?.slice(-1)[0]?.by;
  const canCounterNow = isNegotiable && (
    offer.status === "pending" ||
    (offer.status === "countered" && lastCounterBy !== counterBy)
  );

  return (
    <div
      className={`border rounded-xl p-4 transition-all ${
        offer.status === "accepted"  ? "border-emerald-400 bg-emerald-50"
        : offer.status === "rejected" ? "border-slate-200 bg-slate-50 opacity-50"
        : offer.status === "countered" ? "border-blue-300 bg-blue-50/40"
        : "border-orange-200 bg-white hover:border-orange-400 hover:shadow-sm"
      }`}
      data-testid={`offer-${offer.offer_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center font-display font-extrabold text-sm text-white flex-shrink-0 shadow-sm">
          {ini}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-slate-900 text-sm">{offer.pro_name}</span>
            {offer.pro_rating > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-600">
                <Stars n={offer.pro_rating} />{offer.pro_rating.toFixed(1)}
              </span>
            )}
            {offer.status === "accepted" && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                <CheckCircle2 className="w-3 h-3" /> Acceptée
              </span>
            )}
            {offer.status === "rejected" && (
              <span className="text-[11px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Refusée</span>
            )}
            {offer.status === "countered" && (
              <span className="text-[11px] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                Négociation en cours
              </span>
            )}
          </div>

          {offer.pro_company && <div className="text-xs text-slate-500 mt-0.5">{offer.pro_company}</div>}
          {offer.message && <p className="text-sm text-slate-700 mt-2 leading-relaxed whitespace-pre-wrap">{offer.message}</p>}

          {/* Historique des contre-offres */}
          {offer.counter_history?.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Historique négociation</div>
              {offer.counter_history.map((c, i) => (
                <div key={i} className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 ${
                  c.by === "client" ? "bg-orange-50 border border-orange-100 ml-4" : "bg-slate-50 border border-slate-100"
                }`}>
                  <span className={`font-bold flex-shrink-0 ${c.by === "client" ? "text-orange-600" : "text-slate-600"}`}>
                    {c.by === "client" ? "Vous" : offer.pro_name}
                  </span>
                  <span className="text-slate-500">propose</span>
                  <span className="font-bold text-slate-900">{c.price_eur.toFixed(0)} €</span>
                  {c.message && <span className="text-slate-400 truncate">— {c.message}</span>}
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> ETA {offer.eta_minutes} min</span>
            <span>·</span>
            <span>{new Date(offer.created_at).toLocaleString("fr-FR", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</span>
          </div>
        </div>

        {/* Prix + actions */}
        <div className="text-right flex-shrink-0">
          <div className="font-display font-extrabold text-2xl text-slate-900">{offer.price_eur.toFixed(0)} €</div>
          {canAccept && isNegotiable && (
            <div className="flex flex-col items-end gap-1.5 mt-2">
              <button
                onClick={() => onAccept(offer.offer_id)}
                className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1"
              >
                <CheckCircle2 className="w-3 h-3" /> Accepter
              </button>
              {canCounterNow && onCounter && (
                <button
                  onClick={() => setShowCounter(v => !v)}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1"
                >
                  <ArrowRight className="w-3 h-3" /> Contre-offre
                </button>
              )}
              {offer.status === "countered" && onAbandon && (
                <button
                  onClick={() => onAbandon(offer.offer_id)}
                  className="px-3 py-1.5 border border-red-200 text-red-500 hover:bg-red-50 rounded-lg text-xs font-bold transition-colors flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Abandonner
                </button>
              )}
              {offer.status !== "countered" && onRefuse && (
                <button
                  onClick={() => onRefuse(offer.offer_id)}
                  className="p-1.5 border border-slate-200 rounded-lg text-slate-400 hover:text-red-500 hover:border-red-200 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Formulaire contre-offre */}
      {showCounter && (
        <div className="mt-4 pt-4 border-t border-blue-200 space-y-3">
          <div className="text-xs font-bold text-blue-700">Votre contre-offre</div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="number"
                min="1"
                value={counterPrice}
                onChange={e => setCounterPrice(e.target.value)}
                placeholder={`Prix actuel : ${offer.price_eur.toFixed(0)} €`}
                className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
            </div>
          </div>
          <input
            type="text"
            value={counterMsg}
            onChange={e => setCounterMsg(e.target.value)}
            placeholder="Message optionnel…"
            className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowCounter(false)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs text-slate-500 hover:bg-slate-50"
            >
              Annuler
            </button>
            <button
              onClick={submitCounter}
              disabled={counterLoading || !counterPrice}
              className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1"
            >
              {counterLoading ? <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="w-3 h-3" />}
              Envoyer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Offer form (pro view) — with price estimator ──────────────────
function OfferForm({ requestId, request, onSubmitted }) {
  const [price,    setPrice]    = useState("");
  const [eta,      setEta]      = useState(60);
  const [message,  setMessage]  = useState("");
  const [loading,  setLoading]  = useState(false);
  const [expanded, setExpanded] = useState(false);

  const est     = useMemo(() => calcEst(request?.distance_km, request?.vehicle_type, request?.urgency), [request]);
  const priceN  = parseFloat(price) || 0;
  const diff    = est && priceN > 0 ? Math.round((priceN - est.med) / est.med * 100) : null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/offers", {
        request_id: requestId,
        price_eur:   parseFloat(price),
        eta_minutes: parseInt(eta, 10),
        message,
      });
      toast.success("Offre envoyée");
      setPrice(""); setEta(60); setMessage("");
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const quickSets = [
    est && { label: `Médiane ${est.med} €`, val: est.med },
    est && { label: `+10%  ${Math.round(est.med * 1.1)} €`, val: Math.round(est.med * 1.1) },
    request?.budget_eur && { label: `Budget client ${request.budget_eur} €`, val: request.budget_eur },
  ].filter(Boolean);

  return (
    <form onSubmit={submit} className="mt-6" data-testid="offer-form">
      <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">

        {/* Header dark */}
        <div className="bg-slate-900 px-5 py-4">
          <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-0.5">Déposer une offre</div>
          <div className="text-white font-display font-bold text-sm leading-tight">
            {request?.pickup?.city} → {request?.dropoff?.city}
            <span className="text-slate-400 font-normal ml-2">{Math.round(request?.distance_km || 0)} km · {request?.vehicle_type}</span>
          </div>
          {request?.budget_eur ? (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[11px] text-slate-400">Budget client :</span>
              <span className="font-display font-extrabold text-orange-400 text-lg">{request.budget_eur} €</span>
            </div>
          ) : (
            <div className="mt-2 text-[11px] text-slate-500 italic">Aucun budget renseigné par le client</div>
          )}
        </div>

        <div className="p-5 bg-white space-y-5">

          {/* Estimation box */}
          {est && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3"
                onClick={() => setExpanded(v => !v)}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <Calculator className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="text-left">
                    <div className="text-xs font-bold text-slate-900">Estimation marché 2026</div>
                    <div className="text-[11px] text-slate-500">Plateau · {est.km} km · {est.min}–{est.max} €</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-display font-extrabold text-xl text-orange-600">{est.med} €</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
                </div>
              </button>

              {expanded && (
                <div className="px-4 pb-4 border-t border-amber-200 pt-3 space-y-2 text-sm">
                  <div className="flex justify-between text-slate-500">
                    <span>Coût estimé (carburant, temps, usure, marge)</span>
                    <span className="font-medium text-slate-700">{est.med} €</span>
                  </div>
                  {est.urgMult !== 1.0 && (
                    <div className="flex justify-between text-slate-500">
                      <span>Coefficient urgence</span>
                      <span className="font-medium text-amber-600">×{est.urgMult}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-amber-200 font-bold">
                    <span className="text-slate-900">Fourchette estimée</span>
                    <span className="text-orange-600 font-display text-base">{est.min}–{est.max} €</span>
                  </div>
                </div>
              )}

              {/* Diff badge */}
              {diff !== null && (
                <div className={`mx-4 mb-3 flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg ${
                  diff >= 0
                    ? "bg-emerald-100 text-emerald-700"
                    : diff >= -15
                    ? "bg-amber-100 text-amber-700"
                    : "bg-red-100 text-red-700"
                }`}>
                  {diff >= 0
                    ? <><CheckCircle2 className="w-3 h-3 flex-shrink-0" />Offre {diff}% au-dessus de la médiane</>
                    : diff >= -15
                    ? <><AlertTriangle className="w-3 h-3 flex-shrink-0" />Offre {Math.abs(diff)}% sous la médiane — recevable</>
                    : <><AlertTriangle className="w-3 h-3 flex-shrink-0" />Offre {Math.abs(diff)}% sous la médiane — peu de réponses</>
                  }
                </div>
              )}
            </div>
          )}

          {/* Quick-set chips */}
          {quickSets.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {quickSets.map(s => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => setPrice(String(s.val))}
                  className={`text-[11px] font-semibold px-3 py-1.5 rounded-full border transition-all ${
                    priceN === s.val
                      ? "border-orange-500 bg-orange-50 text-orange-700"
                      : "border-slate-200 bg-white text-slate-500 hover:border-orange-300 hover:text-orange-600"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {/* Price + ETA row */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold text-slate-600 mb-1.5 block">
                Mon prix (€) <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  type="number" min="1" step="1" required
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  className="pl-8 h-12 text-lg font-display font-bold border-slate-300 focus-visible:ring-orange-500"
                  placeholder={est ? String(est.med) : "0"}
                  data-testid="offer-price"
                />
                <Euro className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold text-slate-600 mb-1.5 block">
                Délai d'intervention (min) <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  type="number" min="1" required
                  value={eta}
                  onChange={e => setEta(e.target.value)}
                  className="pl-8 h-12 border-slate-300 focus-visible:ring-orange-500"
                  data-testid="offer-eta"
                />
                <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Message */}
          <div>
            <Label className="text-xs font-bold text-slate-600 mb-1.5 block">Message au client</Label>
            <Textarea
              rows={3}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Disponible immédiatement avec plateau 3,5t. Je suis à 15 min de votre position."
              className="border-slate-300 focus-visible:ring-orange-500 resize-none text-sm"
              data-testid="offer-message"
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !price}
            className="w-full h-12 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl font-display font-bold text-sm flex items-center justify-center gap-2 transition-colors relative overflow-hidden"
            data-testid="submit-offer"
            style={{ background: !loading && price ? undefined : undefined }}
          >
            <span className="absolute inset-0 pointer-events-none" style={{background:"linear-gradient(135deg,rgba(249,115,22,.15),transparent 60%)"}} />
            {loading
              ? "Envoi en cours…"
              : <><Send className="w-4 h-4" />Envoyer mon offre{price ? ` · ${price} €` : ""}</>
            }
          </button>
        </div>
      </div>
    </form>
  );
}

// ── Photo upload helper ───────────────────────────────────────────
function PhotoUpload({ photos, onAdd, onRemove, max, label }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700 mb-2">
        {label} <span className="text-slate-400 font-normal">({photos.length}/{max})</span>
      </div>
      <div className="flex gap-2 flex-wrap">
        {photos.map((p, i) => (
          <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200 flex-shrink-0">
            <img src={p.url} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onRemove(i)}
              className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/60 rounded-full flex items-center justify-center">
              <X className="w-2.5 h-2.5 text-white" />
            </button>
          </div>
        ))}
        {photos.length < max && (
          <label className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-200 flex flex-col items-center justify-center cursor-pointer bg-slate-50 hover:bg-orange-50 hover:border-orange-300 transition-colors flex-shrink-0">
            <Camera className="w-4 h-4 text-slate-400" />
            <span className="text-[10px] text-slate-400 mt-1">Ajouter</span>
            <input type="file" accept="image/*" className="hidden" onChange={e => {
              const f = e.target.files[0]; if (!f) return;
              onAdd({ url: URL.createObjectURL(f), name: f.name, file: f });
              e.target.value = "";
            }} />
          </label>
        )}
      </div>
    </div>
  );
}

// ── Galerie des photos contradictoires (visible client ET convoyeur) ──
function photoSrc(url) {
  // Les URLs API sont relatives ("/api/photos/...") : on les résout sur le backend
  return url && url.startsWith("/api/") ? API_BASE + url.slice(4) : url;
}

function GateGallery({ gates, phase, label }) {
  const items = (gates || []).filter(g => g.phase === phase);
  if (!items.length) return null;
  return (
    <div className="space-y-2">
      {items.map(g => (
        <div key={g.gate_id}>
          <div className="text-[11px] font-semibold text-slate-600 flex items-center gap-1.5 mb-1.5">
            <Camera className="w-3.5 h-3.5 text-slate-400" />
            {label} · {formatDate(g.uploaded_at)}
          </div>
          <div className="flex gap-2 flex-wrap">
            {g.photos.map(p => (
              <a
                key={p.index}
                href={photoSrc(p.url)}
                target="_blank"
                rel="noreferrer"
                title="Ouvrir en grand"
                className="block w-20 h-20 rounded-lg overflow-hidden border border-slate-200 hover:border-orange-400 transition-colors flex-shrink-0"
              >
                <img src={photoSrc(p.url)} alt={`${label} ${p.index + 1}`} loading="lazy" className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


// ── Dossier contradictoire du litige : chaque partie verse sa version ──
function DisputePanel({ requestId, role }) {
  const [dossier, setDossier] = useState(null);
  const [msg, setMsg] = useState("");
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);

  const loadDossier = async () => {
    try {
      const { data } = await api.get(`/requests/${requestId}/dispute`);
      setDossier(data);
    } catch {}
  };
  useEffect(() => { loadDossier(); }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!dossier) return null;
  const open = ["OPEN", "ESCALATED"].includes(dossier.status);
  const isPro = role === "pro";

  const submit = async () => {
    if (!msg.trim() && photos.length === 0) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("message", msg.trim());
      photos.forEach(ph => form.append("files", ph.file, ph.name));
      await api.post(`/requests/${requestId}/dispute-evidence`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Votre version a été versée au dossier — pièces scellées SHA-256");
      setMsg("");
      setPhotos([]);
      await loadDossier();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const BY_UI = {
    client: ["Client", "border-blue-200 bg-blue-50"],
    transporter: ["Dépanneur", "border-orange-200 bg-orange-50"],
    admin: ["Médiation", "border-slate-200 bg-slate-50"],
  };

  return (
    <div className="border border-red-200 bg-white rounded-xl p-4 space-y-3" data-testid="dispute-panel">
      <div className="text-[10px] uppercase tracking-widest text-red-600 font-bold">Dossier de litige</div>
      <div className="text-xs text-slate-700 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
        <span className="font-bold">Motif de la contestation :</span> {dossier.reason}
      </div>

      {dossier.evidence.length > 0 && (
        <div className="space-y-2">
          {dossier.evidence.map(ev => {
            const [who, cls] = BY_UI[ev.by] || [ev.by, "border-slate-200 bg-slate-50"];
            return (
              <div key={ev.evidence_id} className={`border rounded-lg p-3 ${cls}`}>
                <div className="text-[11px] font-bold text-slate-700 mb-1">
                  {who}{ev.author_name ? ` — ${ev.author_name}` : ""} · {formatDate(ev.created_at)}
                </div>
                {ev.message && <div className="text-xs text-slate-700 whitespace-pre-wrap">{ev.message}</div>}
                {ev.photos.length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-2">
                    {ev.photos.map(ph => (
                      <a key={ph.index} href={photoSrc(ph.url)} target="_blank" rel="noreferrer" title="Ouvrir en grand"
                         className="block w-16 h-16 rounded-lg overflow-hidden border border-slate-200 hover:border-orange-400 transition-colors">
                        <img src={photoSrc(ph.url)} alt="" loading="lazy" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="space-y-2 pt-2 border-t border-slate-100">
          <div className="text-xs font-semibold text-slate-700">
            {isPro ? "Votre réponse : version des faits et pièces" : "Complétez votre dossier : photos du problème constaté"}
          </div>
          <textarea
            value={msg}
            onChange={e => setMsg(e.target.value)}
            maxLength={1000}
            rows={3}
            placeholder="Décrivez votre version des faits…"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-orange-400"
          />
          <PhotoUpload
            photos={photos}
            onAdd={ph => setPhotos(prev => [...prev, ph])}
            onRemove={i => setPhotos(prev => prev.filter((_, j) => j !== i))}
            max={6}
            label="Photos justificatives"
          />
          <button
            onClick={submit}
            disabled={sending || (!msg.trim() && photos.length === 0)}
            className="w-full py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-bold text-xs transition-colors"
            data-testid="submit-evidence"
          >
            {sending ? "Envoi…" : "Verser au dossier"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Mission tracker v2.0 — piloté par la machine à états (photo-gating réel) ──
function MissionTracker({ req, offer, role = "client", missionInfo, onChanged }) {
  const isPro  = role === "pro";
  const m = missionInfo?.mission;
  const p = missionInfo?.payment;

  const [pickupPhotos,   setPickupPhotos]   = useState([]);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [gates,   setGates]   = useState([]);

  const missionState = missionInfo?.mission?.state;
  useEffect(() => {
    // Les photos n'existent qu'à partir du chargement — inutile d'appeler avant
    if (!missionState || ["CREATED", "VALIDATED", "AWAITING_BALANCE"].includes(missionState)) {
      setGates([]);
      return;
    }
    let alive = true;
    api.get(`/requests/${req.request_id}/photos`)
      .then(({ data }) => { if (alive) setGates(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [req.request_id, missionState]);

  if (!m) return null;
  const state = m.state;

  const amount = p?.amount_total_cents ? p.amount_total_cents / 100 : (offer?.price_eur || 0);
  const fee    = p?.amount_commission_cents ? p.amount_commission_cents / 100 : 0;
  const proNet = p?.amount_transporter_cents ? p.amount_transporter_cents / 100 : amount;

  const escrowPending   = ["CREATED", "VALIDATED", "AWAITING_BALANCE"].includes(state);
  const pickupActive    = state === "AWAITING_PICKUP";
  const pickupDone      = !escrowPending && !pickupActive; // LOADED et au-delà
  const inTransit       = state === "LOADED";
  const awaitingConfirm = state === "DELIVERY_PENDING_CONFIRMATION";
  const released        = ["DELIVERED", "SETTLED"].includes(state);
  const disputed        = ["DISPUTED", "DISPUTE_ESCALATED", "DISPUTE_RESOLVED"].includes(state);
  const deadline        = m.confirmation_deadline ? new Date(m.confirmation_deadline) : null;

  const uploadPhotos = async (phase, photos, reset) => {
    setSending(true);
    try {
      const form = new FormData();
      photos.forEach(ph => form.append("files", ph.file, ph.name));
      await api.post(`/requests/${req.request_id}/photos/${phase}`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(phase === "depart"
        ? "Prise en charge confirmée — photos scellées (SHA-256)"
        : "Livraison documentée — le client peut confirmer ou contester");
      reset([]);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const confirmDelivery = async () => {
    setSending(true);
    try {
      await api.post(`/requests/${req.request_id}/confirm-delivery`);
      toast.success("Livraison confirmée — fonds reversés au dépanneur");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const contestDelivery = async () => {
    const reason = window.prompt("Décrivez précisément le problème constaté (état du véhicule, délais…) :");
    if (!reason || reason.trim().length < 5) return;
    setSending(true);
    try {
      await api.post(`/requests/${req.request_id}/contest-delivery`, { reason: reason.trim() });
      toast.info("Contestation enregistrée — fonds gelés, médiation sous 7 jours ouvrés");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const steps = [
    { label: "Séquestre",       done: !escrowPending, active: escrowPending },
    { label: "Prise en charge", done: pickupDone,     active: pickupActive  },
    { label: "Livraison",       done: released,       active: inTransit || awaitingConfirm },
  ];

  return (
    <div className="border-2 border-orange-400 rounded-2xl p-4 space-y-4">

      {/* Dark header */}
      <div className="bg-slate-900 rounded-xl p-5 relative overflow-hidden">
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-orange-500/10" />
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-3">
          MISSION · {req.client_name}
        </div>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1">
            <div className="font-display font-extrabold text-white text-xl">{req.pickup.city}</div>
          </div>
          <ArrowRight className="w-5 h-5 text-orange-500 flex-shrink-0" />
          <div className="flex-1 text-right">
            <div className="font-display font-extrabold text-white text-xl">{req.dropoff.city}</div>
          </div>
        </div>
        <div className={`grid ${isPro ? "grid-cols-3" : "grid-cols-2"} gap-3`}>
          {(isPro ? [
            ["CONSIGNÉ",    `${amount.toFixed(2)} €`, "text-orange-400" ],
            ["COMMISSION",  `-${fee.toFixed(2)} €`,   "text-red-400"    ],
            ["NET TRANSP.", `${proNet.toFixed(2)} €`, "text-emerald-400"],
          ] : [
            ["CONSIGNÉ",  `${amount.toFixed(2)} €`,                 "text-orange-400" ],
            ["DÉBLOCAGE", released ? "Effectué" : "Après livraison", "text-emerald-400"],
          ]).map(([label, val, cls]) => (
            <div key={label} className="bg-white/5 rounded-lg px-3 py-2">
              <div className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mb-1">{label}</div>
              <div className={`font-display font-extrabold text-base ${cls}`}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Progress stepper */}
      <div className="flex items-start">
        {steps.map((s, i) => (
          <div key={s.label} className="flex-1 flex flex-col gap-1.5">
            <div className="flex items-center">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold z-10 ${
                s.done ? "bg-emerald-500 text-white" : s.active ? "bg-orange-500 text-white" : "bg-slate-200 text-slate-400"
              }`}>
                {s.done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-0.5 ${s.done ? "bg-emerald-400" : "bg-slate-200"}`} />
              )}
            </div>
            <span className={`text-[11px] font-semibold ${
              s.done ? "text-emerald-600" : s.active ? "text-orange-600" : "text-slate-400"
            }`}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Litige : gel sélectif + dossier contradictoire ── */}
      {disputed && (
        <>
          <div className="border border-red-300 bg-red-50 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-red-700">
              <div className="font-display font-bold text-sm mb-0.5">Livraison contestée — fonds gelés</div>
              La médiation tranche sur pièces : versez votre version des faits et vos photos
              au dossier ci-dessous (instruction sous 7 jours ouvrés).
            </div>
          </div>
          <DisputePanel requestId={req.request_id} role={role} />
        </>
      )}

      {/* ── Prise en charge ── */}
      <div className={`border rounded-xl p-4 transition-all ${
        pickupDone ? "border-emerald-400 bg-emerald-50" : pickupActive ? "border-orange-300 bg-white" : "border-slate-200 bg-slate-50 opacity-70"
      }`}>
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            pickupDone ? "bg-emerald-100" : "bg-orange-100"
          }`}>
            <Truck className={`w-4 h-4 ${pickupDone ? "text-emerald-600" : "text-orange-600"}`} />
          </div>
          <div>
            <div className={`font-display font-bold text-sm ${pickupDone ? "text-emerald-700" : "text-slate-900"}`}>
              Prise en charge
            </div>
            <div className="text-xs text-slate-500">Photos contradictoires au chargement (état départ)</div>
          </div>
          {pickupDone && (
            <span className="ml-auto text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Documentée
            </span>
          )}
        </div>

        {escrowPending ? (
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" /> En attente du séquestre complet (solde débité automatiquement à J-3).
          </div>
        ) : pickupDone ? (
          <div className="space-y-3">
            <div className="text-xs text-emerald-600 font-medium flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> Véhicule pris en charge — photos scellées SHA-256
            </div>
            <GateGallery gates={gates} phase="DEPART" label="Photos au chargement" />
          </div>
        ) : isPro ? (
          /* Vue PRO — photos départ -> LOADED */
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Photographiez l'état du véhicule au chargement (2 photos minimum). Preuve contradictoire obligatoire.
            </p>
            <PhotoUpload
              photos={pickupPhotos}
              onAdd={ph => setPickupPhotos(prev => [...prev, ph])}
              onRemove={i => setPickupPhotos(prev => prev.filter((_, j) => j !== i))}
              max={4}
              label="Photos chargement"
            />
            <button
              onClick={() => uploadPhotos("depart", pickupPhotos, setPickupPhotos)}
              disabled={pickupPhotos.length < 2 || sending}
              className="w-full py-3 rounded-xl font-display font-bold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: pickupPhotos.length < 2 ? "#F8E3BC" : "#E8A020", color: "#fff" }}
              data-testid="upload-depart"
            >
              <Camera className="w-4 h-4" /> {sending ? "Envoi…" : "Confirmer la prise en charge"}
            </button>
          </div>
        ) : (
          /* Vue client — lecture seule */
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" /> En attente du chargement par le dépanneur (photos contradictoires).
          </div>
        )}
      </div>

      {/* ── Livraison ── */}
      <div className={`border rounded-xl p-4 transition-all ${
        released ? "border-emerald-400 bg-emerald-50"
        : (inTransit || awaitingConfirm) ? "border-orange-300 bg-white"
        : "border-slate-200 bg-slate-50 opacity-60"
      }`}>
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            released ? "bg-emerald-100" : pickupDone ? "bg-orange-100" : "bg-slate-100"
          }`}>
            <CheckCircle2 className={`w-4 h-4 ${
              released ? "text-emerald-600" : pickupDone ? "text-orange-600" : "text-slate-400"
            }`} />
          </div>
          <div>
            <div className={`font-display font-bold text-sm ${
              released ? "text-emerald-700" : pickupDone ? "text-slate-900" : "text-slate-400"
            }`}>
              Livraison &amp; déblocage
            </div>
            <div className="text-xs text-slate-500">
              {isPro ? `Net reversé après validation : ${proNet.toFixed(2)} €` : "Validation sous 72 h ou libération automatique"}
            </div>
          </div>
          {released && (
            <span className="ml-auto text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> {state === "SETTLED" ? "Fonds reversés" : "Déblocage en cours"}
            </span>
          )}
        </div>

        {!pickupDone ? (
          <div className="text-xs text-slate-400 flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" /> Déverrouillé après prise en charge.
          </div>
        ) : released ? (
          <div className="space-y-3">
            <div className="text-xs text-emerald-600 font-medium flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {state === "SETTLED"
                ? (isPro ? `Mission clôturée — ${proNet.toFixed(2)} € nets, versement T+2/T+3` : "Mission clôturée — fonds reversés au dépanneur")
                : "Livraison validée — transfert des fonds en cours"}
            </div>
            <GateGallery gates={gates} phase="ARRIVEE" label="Photos à la livraison" />
          </div>
        ) : disputed ? (
          <div className="space-y-3">
            <div className="text-xs text-red-600 font-medium flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> Litige ouvert — voir le bandeau ci-dessus.
            </div>
            <GateGallery gates={gates} phase="ARRIVEE" label="Photos à la livraison" />
          </div>
        ) : awaitingConfirm ? (
          isPro ? (
            <div className="space-y-3">
              <div className="text-xs text-slate-600 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-orange-500" />
                Photos transmises. Sans contestation du client, vos fonds partent automatiquement
                {deadline ? <> le <strong>{deadline.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</strong></> : " sous 72 h"}.
              </div>
              <GateGallery gates={gates} phase="ARRIVEE" label="Photos à la livraison" />
            </div>
          ) : (
            /* Client : confirmer ou contester dans le délai */
            <div className="space-y-3">
              <p className="text-xs text-slate-600">
                Le dépanneur a livré votre véhicule. Vérifiez son état sur les photos ci-dessous :
                sans action de votre part{deadline ? <> avant le <strong>{deadline.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</strong></> : " sous 72 h"},
                la livraison sera réputée conforme et le paiement libéré.
              </p>
              <GateGallery gates={gates} phase="ARRIVEE" label="Photos à la livraison" />
              <div className="flex gap-2">
                <button
                  onClick={confirmDelivery}
                  disabled={sending}
                  className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-display font-bold text-sm flex items-center justify-center gap-2"
                  data-testid="confirm-delivery"
                >
                  <CheckCircle2 className="w-4 h-4" /> Confirmer la livraison
                </button>
                <button
                  onClick={contestDelivery}
                  disabled={sending}
                  className="px-4 py-3 rounded-xl border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 font-display font-bold text-sm flex items-center justify-center gap-2"
                  data-testid="contest-delivery"
                >
                  <AlertTriangle className="w-4 h-4" /> Contester
                </button>
              </div>
            </div>
          )
        ) : isPro ? (
          /* Vue PRO — photos arrivée -> délai de contestation */
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Photographiez l'état du véhicule à la livraison (2 photos minimum).
              Déclenche le délai de contestation de 72 h, puis la libération de {proNet.toFixed(2)} €.
            </p>
            <PhotoUpload
              photos={deliveryPhotos}
              onAdd={ph => setDeliveryPhotos(prev => [...prev, ph])}
              onRemove={i => setDeliveryPhotos(prev => prev.filter((_, j) => j !== i))}
              max={4}
              label="Photos livraison"
            />
            <button
              onClick={() => uploadPhotos("arrivee", deliveryPhotos, setDeliveryPhotos)}
              disabled={deliveryPhotos.length < 2 || sending}
              className="w-full py-3 rounded-xl font-display font-bold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: deliveryPhotos.length < 2 ? "#F8E3BC" : "#E8A020", color: "#fff" }}
              data-testid="upload-arrivee"
            >
              <Camera className="w-4 h-4" /> {sending ? "Envoi…" : "Confirmer la livraison"}
            </button>
          </div>
        ) : (
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" /> Transport en cours — photos de livraison à venir.
          </div>
        )}
      </div>
    </div>
  );
}


function ReviewForm({ requestId, onSubmitted }) {
  const [rating,  setRating]  = useState(5);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/reviews", { request_id: requestId, rating, comment });
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 p-4 border border-amber-300 bg-amber-50 rounded-xl" data-testid="review-form">
      <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-2">Votre avis sur la mission</div>
      <div className="flex items-center gap-1 mb-3">
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" onClick={() => setRating(n)} data-testid={`rating-${n}`}>
            <Star className={`h-7 w-7 ${n <= rating ? "fill-amber-500 text-amber-500" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <Textarea
        rows={3}
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder="Professionnel ponctuel, véhicule pris en charge avec soin…"
      />
      <Button
        type="submit"
        disabled={loading}
        className="mt-3 bg-orange-600 hover:bg-orange-700 text-white"
        data-testid="submit-review"
      >
        {loading ? "Envoi…" : "Publier mon avis"}
      </Button>
    </form>
  );
}

// ── Main component ────────────────────────────────────────────────
export default function RequestDetail() {
  const { id }     = useParams();
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const [req,          setReq]         = useState(null);
  const [matches,      setMatches]     = useState([]);
  const [myReview,     setMyReview]    = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [mandateOffer, setMandateOffer] = useState(null);
  const [missionInfo, setMissionInfo] = useState(null);
  const [cancelPlan, setCancelPlan] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/requests/${id}`);
      setReq(data);
      try {
        const m = await api.get(`/requests/${id}/mission`);
        setMissionInfo(m.data);
      } catch {
        setMissionInfo(null); // pro non assigné, ou mission absente (donnée legacy)
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur de chargement");
    }
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (user?.role === "pro" && req) {
      api.get(`/requests/${id}/matches`).then(r => setMatches(r.data)).catch(() => {});
    }
  }, [id, user?.role, req]);

  if (!req) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex pt-16">
          <Sidebar />
          <main className="flex-1 p-8 text-slate-500">Chargement…</main>
        </div>
      </div>
    );
  }

  const isClient     = user?.role === "client" && req.client_id === user.user_id;
  const isPro        = user?.role === "pro";
  const acceptedOffer = req.offers?.find(o => o.status === "accepted");
  const routes       = [{ pickup: req.pickup, dropoff: req.dropoff }];
  const canSubmitOffer = isPro && (
    req.status === "open" ||
    (req.status === "offered" && !req.offers?.some(o => o.pro_id === user.user_id))
  );
  // Le PRO voit le tracker quand son offre est acceptée
  const proHasAccepted = isPro && req.offers?.some(
    o => o.pro_id === user.user_id && o.status === "accepted"
  );

  // Lot 5 : annulation possible tant que le véhicule n'est pas chargé
  const CANCELLABLE_STATES = ["CREATED", "VALIDATED", "AWAITING_BALANCE", "BALANCE_CAPTURED", "AWAITING_PICKUP"];
  const mState = missionInfo?.mission?.state;
  const canClientCancel = isClient && !["cancelled", "completed"].includes(req.status)
    && (!mState || CANCELLABLE_STATES.includes(mState));
  const canProCancel = proHasAccepted && CANCELLABLE_STATES.includes(mState);

  // Accepter = signer le mandat puis payer l'acompte (séquestre v2.0)
  const accept = (offer_id) => {
    const offer = req.offers?.find(o => o.offer_id === offer_id);
    if (offer) setMandateOffer(offer);
  };

  const confirmAccept = async () => {
    if (!mandateOffer) return;
    setPaymentLoading(true);
    try {
      const { data } = await api.post(`/offers/${mandateOffer.offer_id}/accept`);
      if (data.checkout_url) {
        window.location.href = data.checkout_url; // Stripe Checkout (acompte + carte enregistrée)
        return;
      }
      setMandateOffer(null);
      if (data.simulated) {
        toast.success("Offre acceptée — acompte séquestré (mode démo, aucun débit réel)");
      } else {
        toast.success("Offre acceptée");
      }
      await load();
    } catch (err) {
      setMandateOffer(null);
      toast.error(formatApiError(err.response?.data?.detail));
      await load();
    } finally {
      setPaymentLoading(false);
    }
  };

  // ── Lot 5 : annulations ──
  const openCancelPreview = async () => {
    try {
      const { data } = await api.get(`/requests/${id}/cancellation-preview`);
      if (!data.cancellable) { toast.error(data.reason); return; }
      setCancelPlan(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const confirmCancel = async () => {
    setPaymentLoading(true);
    try {
      await api.delete(`/requests/${id}`);
      toast.success("Demande annulée");
      setCancelPlan(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPaymentLoading(false);
    }
  };

  const transporterCancel = async () => {
    if (!window.confirm(
      "Annuler cette mission ?\n\nConséquences (CGU art. 8) : pénalité financière retenue sur vos prochains reversements, " +
      "malus de classement, et suspension au-delà de 3 annulations sur 90 jours. " +
      "Le client sera intégralement remboursé et la mission republiée en priorité."
    )) return;
    setPaymentLoading(true);
    try {
      const { data } = await api.post(`/requests/${id}/transporter-cancel`);
      toast.info(`Mission annulée — pénalité ${(data.penalty_cents / 100).toFixed(2)} €, malus −${data.score_malus} pts`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPaymentLoading(false);
    }
  };

  // Relance de l'encaissement de l'acompte (Checkout quitté, session expirée…)
  const payDeposit = async () => {
    setPaymentLoading(true);
    try {
      const { data } = await api.post(`/requests/${id}/deposit-session`);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      if (data.simulated) toast.success("Acompte séquestré (mode démo, aucun débit réel)");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPaymentLoading(false);
    }
  };

  const counter = async (offer_id, price_eur, message) => {
    try {
      await api.post(`/offers/${offer_id}/counter`, { price_eur, message });
      toast.success("Contre-offre envoyée");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const abandon = async (offer_id) => {
    try {
      await api.post(`/offers/${offer_id}/abandon`);
      toast.info("Négociation abandonnée");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const complete = async () => {
    try {
      await api.post(`/requests/${id}/complete`);
      toast.success("Mission marquée comme terminée");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="request-detail-page">
      {mandateOffer && (
        <MandateModal
          offer={mandateOffer}
          request={req}
          loading={paymentLoading}
          onConfirm={confirmAccept}
          onClose={() => setMandateOffer(null)}
        />
      )}
      {cancelPlan && (
        <CancelModal
          plan={cancelPlan}
          loading={paymentLoading}
          onConfirm={confirmCancel}
          onClose={() => setCancelPlan(null)}
        />
      )}
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8">

          {/* Title row */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <UrgencyBadge urgency={req.urgency} />
            <StatusBadge status={req.status} />
            {req.priority && (
              <span className="text-[10px] font-bold bg-orange-600 text-white px-2 py-0.5 rounded-full uppercase tracking-widest">
                Republiée · prioritaire
              </span>
            )}
            <span className="text-xs text-slate-500 ml-2">#{req.request_id}</span>
            <div className="ml-auto flex gap-2">
              {canClientCancel && (
                <button
                  onClick={openCancelPreview}
                  className="text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 rounded-lg px-3 py-1.5 transition-colors"
                  data-testid="client-cancel"
                >
                  Annuler la demande
                </button>
              )}
              {canProCancel && (
                <button
                  onClick={transporterCancel}
                  disabled={paymentLoading}
                  className="text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50 rounded-lg px-3 py-1.5 transition-colors"
                  data-testid="pro-cancel"
                >
                  Annuler la mission
                </button>
              )}
            </div>
          </div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 tracking-tight">{req.title}</h1>

          <div className="grid lg:grid-cols-5 gap-6 mt-6">

            {/* ── Main column ── */}
            <div className="lg:col-span-3 space-y-6">

              {/* Map + details */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <RouteMap routes={routes} height={300} />
                <div className="p-5 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Départ</div>
                    <div className="font-display font-bold text-slate-900 flex items-center gap-1">
                      <MapPin className="h-4 w-4 text-orange-600" />{req.pickup.city}
                    </div>
                    <div className="text-xs text-slate-500">{req.pickup.country}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Arrivée</div>
                    <div className="font-display font-bold text-slate-900 flex items-center gap-1">
                      <MapPin className="h-4 w-4 text-slate-700" />{req.dropoff.city}
                    </div>
                    <div className="text-xs text-slate-500">{req.dropoff.country}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Distance</div>
                    <div className="flex items-center gap-1 text-slate-900 font-semibold">
                      <Route className="h-4 w-4" />{Math.round(req.distance_km)} km
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Date</div>
                    <div className="flex items-center gap-1 text-slate-900 font-semibold">
                      <Calendar className="h-4 w-4" />{formatDate(req.pickup_date)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Véhicule</div>
                    <div className="flex items-center gap-1 text-slate-900 font-semibold">
                      <Truck className="h-4 w-4" />
                      {req.vehicle_type}
                      {req.vehicle_brand ? ` · ${req.vehicle_brand}` : ""}
                      {req.vehicle_model ? ` ${req.vehicle_model}` : ""}
                    </div>
                  </div>
                  {req.budget_eur && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Budget</div>
                      <div className="flex items-center gap-1 text-slate-900 font-semibold">
                        <Euro className="h-4 w-4" />{req.budget_eur} €
                      </div>
                    </div>
                  )}
                </div>
                {req.description && (
                  <div className="px-5 pb-5">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Description</div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{req.description}</p>
                  </div>
                )}
              </div>

              {/* ── Offers section ── */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Offres</div>
                    <h2 className="font-display font-bold text-xl text-slate-900">
                      {req.offers?.length || 0} offre(s) reçue(s)
                    </h2>
                  </div>
                  {isClient && req.status === "accepted" && missionInfo?.mission?.state === "CREATED" && (
                    <button
                      onClick={payDeposit}
                      disabled={paymentLoading}
                      className="flex items-center gap-2 px-4 py-2 bg-[#161712] hover:bg-[#2A2C25] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-display font-bold text-sm transition-colors"
                      data-testid="pay-button"
                    >
                      {paymentLoading ? (
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Lock className="w-4 h-4" />
                      )}
                      Payer l'acompte
                    </button>
                  )}
                </div>

                {/* ── Mission tracker post-paiement ── */}
                {isClient && req.status === "in_progress" && acceptedOffer ? (
                  <MissionTracker req={req} offer={acceptedOffer} role="client" missionInfo={missionInfo} onChanged={load} />
                ) : proHasAccepted && (req.status === "accepted" || req.status === "in_progress") ? (
                  <MissionTracker req={req} offer={acceptedOffer} role="pro" missionInfo={missionInfo} onChanged={load} />
                ) : (
                  <>
                    {(!req.offers || req.offers.length === 0) && (
                      <div className="py-8 text-center text-slate-400 text-sm">
                        Pas encore d'offre. Les pros réagissent en général en quelques minutes.
                      </div>
                    )}

                    <div className="space-y-3" data-testid="offers-list">
                      {req.offers?.map(o => (
                        <OfferCard
                          key={o.offer_id}
                          offer={o}
                          canAccept={isClient && req.status !== "accepted" && req.status !== "cancelled"}
                          onAccept={accept}
                          onRefuse={isClient ? async (id) => { await api.post(`/offers/${id}/reject`).catch(() => {}); await load(); } : null}
                          onCounter={counter}
                          onAbandon={abandon}
                          counterBy={isClient ? "client" : "pro"}
                        />
                      ))}
                    </div>
                  </>
                )}

                {canSubmitOffer && (
                  <OfferForm requestId={req.request_id} request={req} onSubmitted={load} />
                )}

                {/* Clôture manuelle : réservée aux missions migrées d'avant le photo-gating */}
                {isClient && req.status === "in_progress" && missionInfo?.mission?.legacy && (
                  <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-center justify-between gap-4">
                    <div className="text-sm text-slate-800">Mission d'avant la mise à jour : validez manuellement une fois terminée.</div>
                    <Button
                      onClick={complete}
                      className="bg-orange-600 hover:bg-orange-700 flex-shrink-0"
                      data-testid="complete-mission"
                    >
                      Marquer terminée
                    </Button>
                  </div>
                )}

                {isClient && req.status === "completed" && !myReview && acceptedOffer && (
                  <ReviewForm requestId={req.request_id} onSubmitted={() => toast.success("Avis publié")} />
                )}
              </div>
            </div>

            {/* ── Sidebar column ── */}
            <aside className="lg:col-span-2 space-y-4">

              {(isClient || proHasAccepted) && (
                <PaymentPanel missionInfo={missionInfo} viewer={isClient ? "client" : "pro"} />
              )}

              {/* Lot 7 : publier le trajet retour de cette mission (double appariement) */}
              {proHasAccepted && ["BALANCE_CAPTURED", "AWAITING_PICKUP", "LOADED"].includes(mState) && (
                <a
                  href={`/pro/trajets-retour?origin=${encodeURIComponent(req.dropoff.city)}&destination=${encodeURIComponent(req.pickup.city)}&from=${req.request_id}`}
                  className="block bg-emerald-50 border border-emerald-200 rounded-xl p-4 hover:border-emerald-400 transition-colors"
                  data-testid="publish-return-link"
                >
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-1">Zéro trajet à vide</div>
                  <div className="font-display font-bold text-sm text-slate-900">
                    Publier mon retour {req.dropoff.city} → {req.pickup.city}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Recevez en priorité les demandes compatibles et monétisez votre retour sur ce même mouvement.
                  </div>
                </a>
              )}

              {isPro && matches.length > 0 && (
                <div className="bg-slate-900 text-white rounded-xl p-5">
                  <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-1">Matching aller/retour</div>
                  <h3 className="font-display font-bold text-xl mb-3">{matches.length} mission(s) compatible(s)</h3>
                  <div className="space-y-2">
                    {matches.slice(0, 5).map(m => (
                      <a
                        key={m.request_id}
                        href={`/requests/${m.request_id}`}
                        className="block bg-slate-800 hover:bg-slate-700 rounded-lg p-3 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm font-medium truncate">{m.pickup.city} → {m.dropoff.city}</div>
                          <span className="shrink-0 text-[11px] font-bold bg-orange-600 px-2 py-0.5 rounded-full">
                            {m.match_type === "reverse" ? "A/R" : "AXE"} {Math.round(m.best_score)}
                          </span>
                        </div>
                        <div className="text-xs text-slate-400 mt-1">{m.title}</div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {isClient && <ClientEstimateBox req={req} />}

              <div className="bg-white border border-slate-200 rounded-xl p-5 text-sm">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Client</div>
                <div className="font-display font-bold text-slate-900">{req.client_name}</div>
                <div className="text-xs text-slate-500 mt-0.5">Publié le {formatDate(req.created_at)}</div>
              </div>
            </aside>
          </div>
        </main>
      </div>
    </div>
  );
}
